import { useEffect, useRef, useState } from "react"
import { router } from "@inertiajs/react"
import { cn } from "@/lib/utils"

interface LanguageSwitcherProps {
  locales: string[]
  current: string
  labels: Record<string, string>
  variant?: "desktop" | "mobile"
}

export default function LanguageSwitcher({
  locales,
  current,
  labels,
  variant = "desktop",
}: LanguageSwitcherProps) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [open])

  const handleSelect = (locale: string) => {
    setOpen(false)
    router.visit(`/locale/${locale}`, {
      method: "patch",
      preserveState: false,
      preserveScroll: true,
    })
  }

  const isDesktop = variant === "desktop"

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "flex items-center text-content hover:text-contrast transition-colors cursor-pointer",
          isDesktop
            ? "gap-2 px-3 py-2 font-medium tracking-wide"
            : "gap-1 px-2 py-1"
        )}
      >
        <i className="fas fa-globe" />
        <span className={cn("uppercase", isDesktop ? "text-sm" : "text-xs")}>
          {current}
        </span>
        {isDesktop && <i className="fas fa-chevron-down text-xs ml-1" />}
      </button>

      <div
        role="menu"
        className={cn(
          "absolute right-0 mt-2 w-48 bg-light border border-contour rounded-lg shadow-lg z-50 overflow-y-auto",
          isDesktop ? "max-h-96" : "max-h-64",
          open ? "" : "hidden"
        )}
      >
        <div className="py-2">
          {locales.map((code) => {
            const isCurrent = code === current
            return (
              <button
                key={code}
                type="button"
                role="menuitem"
                onClick={() => handleSelect(code)}
                className={cn(
                  "w-full text-left px-4 py-2 text-sm hover:bg-contour transition-colors flex items-center justify-between",
                  isCurrent ? "text-contrast font-medium" : "text-content"
                )}
              >
                <span>{labels[code] ?? code}</span>
                {isCurrent && <i className="fas fa-check text-contrast" />}
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}
